import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  Subject,
  catchError,
  concatMap,
  forkJoin,
  map,
  of,
  switchMap,
} from 'rxjs';
import { OfficePracticeAreaSettingDto } from '../../../models/office-practice-area-setting.models';
import { PracticeArea } from '../../../models/user.models';
import { AuthService } from '../../../services/auth.service';
import { OfficePracticeAreaSettingsService } from '../../../services/office-practice-area-settings.service';
import { PracticeAreaService } from '../../../services/practice-area.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { describeApiError } from '../../../utils/api-error.utils';

/** A firm-wide practice area paired with this office's settings for it. */
interface PracticeAreaSettingRow {
  code: string;
  name: string;
  /** Weeks per capacity period for resources whose primary practice area this is (1 or 2). */
  weeks: number;
  /** The last period length the server acknowledged — the rollback target for `weeks`. */
  savedWeeks: number;
  /** Saves of `weeks` that are queued or in flight; only the last of them owns the control. */
  pendingWeeks: number;
  required: boolean;
  /**
   * The last value the server acknowledged for this pairing. A failed save rolls `required` back to
   * this rather than negating whatever is on screen, which was order-dependent.
   */
  savedRequired: boolean;
  /** Saves of `required` that are queued or in flight; only the last of them owns the control. */
  pendingRequired: number;
}

/**
 * One queued save: the single setting the click changed, in the shape the API's partial-update
 * body carries it. Whatever this omits keeps its stored value, so the two controls on a row can
 * never publish each other's state.
 *
 * A union rather than two optional properties: a change carrying *both* settings would type-check
 * against a `Pick` of optionals, and the whole point of this shape is that it cannot exist.
 */
type QueuedSettingChange =
  | { expectedHoursIsRequired: boolean }
  | { capacityIntervalWeeks: number };

/** The success snackbar for a save, naming the practice area and the one setting it carried. */
function savedMessage(
  practiceAreaName: string,
  change: QueuedSettingChange,
): string {
  if ('capacityIntervalWeeks' in change) {
    const weeks = change.capacityIntervalWeeks;
    const period = weeks === 1 ? '1 week' : `${weeks} weeks`;
    return `${practiceAreaName}: capacity period set to ${period}`;
  }

  return change.expectedHoursIsRequired
    ? `${practiceAreaName}: expected hours now required`
    : `${practiceAreaName}: expected hours no longer required`;
}

/**
 * Table columns while the expected-hours feature flag is off — the period column alone.
 *
 * Immutable, and copied wherever it is handed to `practiceAreaColumns`: the field is public, so
 * a `push` on it would otherwise mutate the constant for every component created afterwards.
 */
const PERIOD_ONLY_COLUMNS = ['practiceArea', 'period'] as const;

/** Every column: the period, then the expected-hours state label and its toggle. Also copied. */
const ALL_COLUMNS = ['practiceArea', 'period', 'state', 'required'] as const;

/**
 * Practice Area Settings card on the office details page. Sets, per practice area in this
 * office, the capacity period resources are on and whether they must supply expected hours
 * when their capacity is saved.
 *
 * Both controls auto-save on change — there is no explicit Save button. Each click upserts the
 * (officeCode, practiceAreaCode) pairing carrying **only** the setting it changed, because the
 * API treats an omitted field as "leave it alone"; turning expected hours off is an upsert with
 * `false`, not a delete, because the API has no DELETE for these settings. The card is
 * read-only for users with view-only permission.
 *
 * The **card itself is not flag-gated** — the cadence rule it feeds is not either, so the
 * period column has to be reachable in production. Only the expected-hours column and its note
 * hang off `expectedHoursEnabled`, which the host binds to the `expected-period-hours` flag.
 * That flag also gates enforcement server-side, in
 * `OfficePracticeAreaSettingService.IsExpectedHoursRequiredAsync`, so while it is off a stored
 * row is inert rather than silently mandatory.
 */
@Component({
  selector: 'app-office-practice-area-settings',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './office-practice-area-settings.component.html',
  styleUrls: ['./office-practice-area-settings.component.scss'],
  animations: [
    // Smoothly expand/collapse the card body, matching the office-notifications card.
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }),
        animate(
          '220ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: '*', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate(
          '180ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: 0, opacity: 0 }),
        ),
      ]),
    ]),
  ],
})
export class OfficePracticeAreaSettingsComponent implements OnInit, OnChanges {
  private readonly service = inject(OfficePracticeAreaSettingsService);
  private readonly practiceAreaService = inject(PracticeAreaService);
  private readonly snackbar = inject(SnackbarService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  /** Office code this card configures (drives every API call). */
  @Input() officeCode = '';

  /** Office name shown in the card's description. */
  @Input() officeDescription = '';

  /**
   * Whether the `expected-period-hours` feature flag is on. Only the expected-hours column and
   * its note depend on it; the period column always renders. The host flips this from false to
   * true once the flag call returns, so it must be re-read on change, not just on init.
   */
  @Input() expectedHoursEnabled = false;

  /** True while the initial settings/practice-area load is in flight. */
  isLoading = false;

  /** Set when the initial load fails; drives the inline error + retry. */
  loadError: string | null = null;

  /** Whether the card body is expanded (the whole card collapses). */
  cardExpanded = true;

  /** Firm-wide practice areas merged with this office's configured settings. */
  practiceAreas: PracticeAreaSettingRow[] = [];

  /** Free-text filter for the practice-area table. */
  practiceAreaFilter = '';

  /** Backs the paginated practice-area table (filtering and paging are client-side). */
  readonly practiceAreaDataSource =
    new MatTableDataSource<PracticeAreaSettingRow>([]);

  /**
   * Columns currently rendered. Held as a field rather than derived in a getter: `matHeaderRowDef`
   * and `matRowDef` re-render whenever the array identity changes, and a getter would hand them a
   * fresh array on every change-detection pass. It is therefore reassigned only when the feature
   * flag changes — and always with a copy of the constant, never the constant itself.
   */
  practiceAreaColumns: string[] = [...PERIOD_ONLY_COLUMNS];

  // Not round-tripped on purpose: the paginator owns its own page size, and a remount
  // deliberately returns to this default rather than restoring the user's last choice.
  readonly practiceAreaPageSize = 5;

  readonly practiceAreaPageSizeOptions = [5, 10, 25, 50];

  private readonly practiceAreaPaginator = viewChild(MatPaginator);

  /** Emits the office code to load. `switchMap` on this discards any load still in flight. */
  private readonly loadOffice$ = new Subject<string>();

  /**
   * One save queue per practice-area code, each serialised with `concatMap`, created lazily on the
   * first change to that row and discarded whenever the loaded rows are replaced.
   */
  private readonly saveQueues = new Map<
    string,
    Subject<QueuedSettingChange>
  >();

  constructor() {
    // The paginator lives inside an @if (card expanded), so it comes and goes. Assign
    // unconditionally rather than only on a truthy value: attaching a new instance and
    // releasing a destroyed one are the same operation. Without the release the data source
    // stays merged onto a dead paginator's `page` stream and keeps writing `length` into it.
    effect(() => {
      this.practiceAreaDataSource.paginator =
        this.practiceAreaPaginator() ?? null;
    });

    // Match on the code and name only. The default predicate stringifies every property, so
    // the `required` flag would leak into matches (typing "true" would filter to the required
    // rows).
    this.practiceAreaDataSource.filterPredicate = (pa, filter) =>
      pa.name.toLowerCase().includes(filter) ||
      pa.code.toLowerCase().includes(filter);

    // switchMap, not a bare subscribe per call: an office switch — or a double-clicked Retry — has
    // to discard the previous request rather than race it. takeUntilDestroyed cancels on destroy
    // only, so without this a slow response for the previous office could land *after* the new
    // office had rendered, and the next toggle would write the old office's flag against the new
    // office's code. That is the same bug ngOnChanges exists to prevent, just moved from "no
    // reload" to "the wrong reload wins".
    this.loadOffice$
      .pipe(
        switchMap((officeCode) =>
          forkJoin({
            settings: this.service.getByOffice(officeCode),
            practiceAreas: this.practiceAreaService.getAllPracticeAreas(),
          }).pipe(
            // Caught inside the inner observable deliberately. An error allowed to reach the outer
            // stream would complete it, and the card would never load again — Retry included.
            catchError((err) => {
              console.error(
                'Error loading office practice area settings:',
                err,
              );
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (!result) {
          this.loadError = 'Failed to load practice area settings.';
          this.isLoading = false;
          return;
        }

        this.practiceAreas = this.mergePracticeAreas(
          result.practiceAreas,
          result.settings,
        );
        this.practiceAreaDataSource.data = this.practiceAreas;
        this.isLoading = false;
      });
  }

  ngOnInit(): void {
    this.applyExpectedHoursColumns();
    this.loadData();
  }

  /**
   * Reload when the host switches office, and re-read the columns when the feature flag lands.
   *
   * The host refreshes in place on a `:officeCode` route-param change rather than re-creating
   * this component (the card's `@if` condition stays true across the change), so `ngOnInit`
   * does not run again. Without this, `officeCode` would update while the loaded rows did not
   * — the table would show the previous office's toggles, and toggling one would write those
   * flags against the new office.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['expectedHoursEnabled']) {
      this.applyExpectedHoursColumns();
    }

    const officeChange = changes['officeCode'];
    if (officeChange && !officeChange.firstChange) {
      // Drop the old office's rows immediately: leaving them on screen during the reload is
      // what made the stale state actionable in the first place.
      this.practiceAreas = [];
      this.practiceAreaDataSource.data = [];
      this.clearPracticeAreaFilter();
      this.loadData();
    }
  }

  loadData(): void {
    if (!this.officeCode) {
      return;
    }

    this.isLoading = true;
    this.loadError = null;
    this.resetSaveQueues();
    this.loadOffice$.next(this.officeCode);
  }

  retry(): void {
    this.loadData();
  }

  /** Adds or drops the expected-hours columns to match the feature flag. */
  private applyExpectedHoursColumns(): void {
    this.practiceAreaColumns = [
      ...(this.expectedHoursEnabled ? ALL_COLUMNS : PERIOD_ONLY_COLUMNS),
    ];
  }

  /** True when the current user may change these settings (else the card is read-only). */
  get canManage(): boolean {
    return this.authService.canManageOfficePracticeAreaSettings();
  }

  get requiredPracticeAreaCount(): number {
    return this.practiceAreas.filter((pa) => pa.required).length;
  }

  /** How many practice areas this office has put on a multi-week capacity period. */
  get twoWeekPracticeAreaCount(): number {
    return this.practiceAreas.filter((pa) => pa.weeks > 1).length;
  }

  onPracticeAreaFilterChange(value: string): void {
    this.practiceAreaFilter = value;
    // MatTableDataSource hands the filter string to the predicate verbatim, so normalize here
    // to keep the predicate a straight case-insensitive comparison.
    this.practiceAreaDataSource.filter = value.trim().toLowerCase();
    // Narrowing the results while parked on a later page would otherwise strand the user on
    // the tail of the matches — MatTableDataSource only clamps pageIndex to the last valid
    // page, it never resets it — so jump back to the first page.
    this.practiceAreaPaginator()?.firstPage();
  }

  /** Clears the filter, which also returns the table to the first page. */
  clearPracticeAreaFilter(): void {
    this.onPracticeAreaFilterChange('');
  }

  /**
   * Auto-save a single practice-area expected-hours toggle.
   *
   * No "ensure the parent exists" step is needed: this upsert is keyed by the (officeCode,
   * practiceAreaCode) pair in the body, so there is no parent row to create first — unlike the
   * office-notifications practice-area endpoint, which hangs off a setting id.
   */
  onExpectedHoursChange(pa: PracticeAreaSettingRow): void {
    if (!this.canManage) {
      return;
    }

    // Queue the value this toggle produced rather than firing straight off. Serialising per row
    // fixes two things at once: the server can no longer end up holding the *older* of two
    // overlapping saves, and a failure has a real value to roll back to.
    //
    // The previous guard compared the row against what the request sent and skipped the rollback
    // if they differed. That was order-dependent — with both saves failing, the second handler
    // found them equal and inverted a row nothing had been written for.
    pa.pendingRequired += 1;
    this.queueFor(pa).next({ expectedHoursIsRequired: pa.required });
  }

  /** Auto-save a single practice area's capacity period. */
  onPeriodChange(pa: PracticeAreaSettingRow): void {
    if (!this.canManage) {
      return;
    }

    pa.pendingWeeks += 1;
    this.queueFor(pa).next({ capacityIntervalWeeks: pa.weeks });
  }

  /**
   * Returns this row's save queue, creating it on first use.
   *
   * `concatMap` runs the row's requests strictly in order, so the last value the user chose is the
   * last one the server sees — and the period and the hours requirement share one queue per row,
   * so two controls on the same row cannot have overlapping upserts of the same pairing in
   * flight. `officeCode` is captured at creation rather than read at send time: queues are
   * discarded whenever the rows are replaced, so a request still draining belongs to the office
   * it was made for, not whichever office is on screen when it goes out.
   *
   * The handler applies the **server's** value, not the one the click sent, and touches only the
   * setting the request carried — the other one may have moved since, with a save of its own
   * already queued. It writes nothing and says nothing once this queue has been detached
   * (`resetSaveQueues`), because by then the row it closes over is off screen.
   */
  private queueFor(pa: PracticeAreaSettingRow): Subject<QueuedSettingChange> {
    const existing = this.saveQueues.get(pa.code);
    if (existing) {
      return existing;
    }

    const officeCode = this.officeCode;
    const queue = new Subject<QueuedSettingChange>();

    queue
      .pipe(
        concatMap((change) =>
          this.service
            .upsert({ officeCode, practiceAreaCode: pa.code, ...change })
            .pipe(
              map((dto) => ({
                change,
                dto: dto as OfficePracticeAreaSettingDto | null,
                error: null as { message?: string } | null,
              })),
              // Caught inside so one failed save does not tear down the row's queue.
              catchError((err: { message?: string }) =>
                of({
                  change,
                  dto: null as OfficePracticeAreaSettingDto | null,
                  error: err ?? {},
                }),
              ),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ change, dto, error }) => {
        // `resetSaveQueues` completes the queue, but cannot cancel a request `concatMap` has
        // already issued: that response still arrives here, against a row the table dropped.
        // Writing to it is invisible, but the snackbar is not — it would announce a save over
        // whichever office is on screen now. Drop the result instead; the request still ran
        // server-side, so nothing is lost and the next load of that office shows the truth.
        if (this.saveQueues.get(pa.code) !== queue) {
          return;
        }

        // Set when this was the last outstanding save for its setting AND the server answered;
        // it carries the acknowledged value, which is what the snackbar reports.
        let settled: QueuedSettingChange | null = null;

        // Two explicit branches rather than a key table: the settings differ only in value type,
        // and that is exactly what a table cannot keep straight without an untyped view of the
        // row. Only the setting this request carried is touched, on either path.
        if ('capacityIntervalWeeks' in change) {
          pa.pendingWeeks -= 1;
          // Nothing newer for this setting is queued, so this response owns the control. While
          // one is, the control belongs to the value the user is waiting on: moving it here
          // would drag it off that value and leave `savedWeeks` disagreeing with the screen —
          // a stale control and a wrong two-week count until the next reload.
          const owner = pa.pendingWeeks === 0;

          if (dto) {
            pa.savedWeeks = dto.capacityIntervalWeeks;
            if (owner) {
              pa.weeks = dto.capacityIntervalWeeks;
              settled = { capacityIntervalWeeks: dto.capacityIntervalWeeks };
            }
          } else if (owner) {
            pa.weeks = pa.savedWeeks;
          }
        } else {
          pa.pendingRequired -= 1;
          const owner = pa.pendingRequired === 0;

          if (dto) {
            pa.savedRequired = dto.expectedHoursIsRequired;
            if (owner) {
              pa.required = dto.expectedHoursIsRequired;
              settled = { expectedHoursIsRequired: dto.expectedHoursIsRequired };
            }
          } else if (owner) {
            pa.required = pa.savedRequired;
          }
        }

        if (error) {
          // Every failure is reported: a save the user made and lost is never silent.
          this.snackbar.showError(
            describeApiError(error, 'Failed to save the practice area setting'),
          );
        } else if (settled) {
          // Only the save that settles the setting confirms it. One toast per queued save would
          // make a rapid 2 → 1 → 2 pop three, the middle one naming a period the toggle no
          // longer shows.
          this.snackbar.showSuccess(savedMessage(pa.name, settled));
        }
      });

    this.saveQueues.set(pa.code, queue);
    return queue;
  }

  /**
   * Detaches every per-row save queue. Called whenever the loaded rows are replaced, because each
   * queue closes over the row object it was created for.
   *
   * Completing a queue only stops *new* work: a request `concatMap` already issued still lands.
   * The guarantee is therefore the handler's, not this method's — dropping the queue from the map
   * is what tells that handler its row is gone, so a late response neither writes to a detached
   * row nor raises a snackbar over the office that replaced it.
   */
  private resetSaveQueues(): void {
    this.saveQueues.forEach((queue) => queue.complete());
    this.saveQueues.clear();
  }

  /**
   * Merge the firm-wide practice areas with this office's configured rows.
   *
   * A practice area with no row takes the defaults — not required, one-week period. The GET
   * returns only configured pairings, so an absent row is the default, not missing data, and
   * the same defaults are what the API resolves to server-side.
   */
  private mergePracticeAreas(
    practiceAreas: PracticeArea[],
    settings: OfficePracticeAreaSettingDto[],
  ): PracticeAreaSettingRow[] {
    const settingByCode = new Map(
      settings.map((s) => [s.practiceAreaCode.toLowerCase(), s]),
    );

    // Deliberately no `isActive` filter on the practice areas. The API never sends the flag —
    // PracticeAreaDto carries no such property — so practice-area.service.ts's
    // `isActive: practiceArea.isActive ?? true` always falls back to true and filtering on it
    // would be a guaranteed no-op.
    //
    // Custom practice areas are left out. Both settings apply by a resource's primary practice
    // area, which always comes from the firm directory sync, so a custom area is never anyone's
    // primary and a setting on one would apply to no one. The API refuses such a save as well.
    const syncedPracticeAreas = practiceAreas.filter((pa) => !pa.isCustom);

    return syncedPracticeAreas.map((pa) => {
      const setting = settingByCode.get(pa.practiceCode.toLowerCase());
      const required = setting?.expectedHoursIsRequired ?? false;
      // `?? 1` also covers a server that predates the column, so the card degrades to weekly
      // rather than rendering an unselected period control.
      const weeks = setting?.capacityIntervalWeeks ?? 1;

      // The saved* fields start equal to what was just read: the server's values by definition,
      // with nothing outstanding against either of them yet.
      return {
        code: pa.practiceCode,
        name: pa.description,
        weeks,
        savedWeeks: weeks,
        pendingWeeks: 0,
        required,
        savedRequired: required,
        pendingRequired: 0,
      };
    });
  }
}
